import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../../../config/axiosConfig";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col } from "react-bootstrap";
import { SlCalender } from "react-icons/sl";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

function Programdetails() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guestCount = Number(queryParams.get("guests")) || 1;
  const selectedProgramId = queryParams.get("program");

  const [guest, setGuest] = useState(Array(guestCount).fill(""));
  const [programs, setPrograms] = useState([]);
  const [selection, setSelection] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minRequiredDate, setMinimumRequiredDate] = useState(0);
  const [residentialType, setResidentialType] = useState([]);
  const [selectedResidentialType, setSelectedResidentialType] = useState("");
  const [residential, setResidential] = useState([]);

  // Fetch programs and related data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/programs");
        const residentialTypeData = await api.get("/residentialTypes");
        const residentialList = await api.get("/residential");

        setPrograms(response.data || []);
        setResidentialType(residentialTypeData.data || []);
        setResidential(residentialList.data || []);

        const initialSelection = Array(guestCount)
          .fill(null)
          .map(() =>
            response.data.map((program) =>
              program.id.toString() === selectedProgramId ? true : false
            )
          );

        setSelection(initialSelection);
      } catch (error) {
        console.error("Error fetching programs:", error);
      }
    };
    fetchData();
  }, [guestCount, selectedProgramId]);

  // Handle guest name changes
  const handleGuestNameChange = (index, value) => {
    const updatedGuest = [...guest];
    updatedGuest[index] = value;
    setGuest(updatedGuest);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (guestIndex, programIndex) => {
    const updatedSelection = [...selection];

    // Uncheck all programs for the current guest
    updatedSelection[guestIndex] = updatedSelection[guestIndex].map(
      () => false
    );

    // Check the selected program
    updatedSelection[guestIndex][programIndex] = true;

    setSelection(updatedSelection);

    // Update minimum required date
    const maxMinDays = updatedSelection
      .map((guestSelection, gIndex) =>
        guestSelection.map((isSelected, progIndex) =>
          isSelected ? programs[progIndex]?.minDays || 0 : 0
        )
      )
      .flat()
      .reduce((max, days) => Math.max(max, days), 0);

    setMinimumRequiredDate(maxMinDays);
  };

  // Handle residential type change
  const handleResidentialTypeChange = (e) => {
    setSelectedResidentialType(e.target.value);
  };

  // Filter residential options based on criteria
  const filteredResidential = residential.filter((room) => {
    const matchesType =
      !selectedResidentialType || room.residentialTypeId === selectedResidentialType;

    const matchesGuestCount = room.capacity >= guestCount;

    const matchesDates =
      (!startDate && !endDate) ||
      (room.availableFrom &&
        room.availableTo &&
        new Date(room.availableFrom) <= endDate &&
        new Date(room.availableTo) >= startDate);

    return matchesType && matchesGuestCount && matchesDates;
  });

  const closeMatches = residential.filter((room) => {
    const closeGuestMatch = room.capacity >= guestCount - 1;
    const closeDateMatch =
      (!startDate && !endDate) ||
      (room.availableFrom &&
        room.availableTo &&
        (new Date(room.availableFrom) <= endDate ||
          new Date(room.availableTo) >= startDate));

    return (
      (!filteredResidential.includes(room) && // Avoid duplication
        closeGuestMatch &&
        closeDateMatch)
    );
  });

  const allMatchingResidential = [...filteredResidential, ...closeMatches];

  return (
    <>
      <div className="programdetailsdiv py-5">
        <div className="container-fluid pb-5 px-md-5 px-3">
          <h2 className="text-white mb-4">Guests and Program Selection</h2>

          <div className="table-responsive">
            <table className="table table-bordered table-dark">
              <thead>
                <tr>
                  <th>Sr. No</th>
                  <th>Guest Name</th>
                  {programs.length > 0 ? (
                    programs.map((program, index) => (
                      <th key={index}>{program.name}</th>
                    ))
                  ) : (
                    <th>No Programs Available</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {guest.map((_, guestIndex) => (
                  <tr key={guestIndex}>
                    <td>{guestIndex + 1}</td>
                    <td>
                      <input
                        type="text"
                        placeholder={`Guest ${guestIndex + 1}`}
                        value={guest[guestIndex]}
                        onChange={(e) =>
                          handleGuestNameChange(guestIndex, e.target.value)
                        }
                        className="form-control"
                      />
                    </td>
                    {programs.length > 0 &&
                      programs.map((_, programIndex) => (
                        <td key={programIndex}>
                          <input
                            type="checkbox"
                            checked={
                              selection[guestIndex]?.[programIndex] || false
                            }
                            onChange={() =>
                              handleCheckboxChange(guestIndex, programIndex)
                            }
                            className="form-check-input p-0"
                          />
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="container-fluid px-md-5 px-3">
          <Row>
            <Col lg={8}>
              <div className="border border-white p-3">
                {minRequiredDate > 0 && (
                  <small className="text-white">
                    Minimum required days for selected program:{" "}
                    {minRequiredDate}
                  </small>
                )}
                <Row>
                  <Col lg={6}>
                    <Row className="d-flex align-items-stretch mt-2">
                      <Col lg={6} className="p-2">
                        <div className="booking-dates p-3 d-flex align-items-center gap-4">
                          <SlCalender className="bookingicons" />
                          <div className="dates-text">
                            <p className="mb-2">
                              <strong>Start Date</strong>
                            </p>
                            <DatePicker
                              selected={startDate}
                              onChange={setStartDate}
                              dateFormat="MMMM d, yyyy"
                              className="form-control date-box rounded-0 border-0 py-2 px-4"
                            />
                          </div>
                        </div>
                      </Col>
                      <Col lg={6} className="p-2">
                        <div className="booking-dates p-3 d-flex align-items-center gap-4">
                          <SlCalender className="bookingicons" />
                          <div className="dates-text">
                            <p className="mb-2">
                              <strong>End Date</strong>
                            </p>
                            <DatePicker
                              selected={endDate}
                              onChange={setEndDate}
                              dateFormat="MMMM d, yyyy"
                              className="form-control date-box rounded-0 border-0 py-2 px-4"
                              minDate={
                                startDate
                                  ? new Date(
                                      startDate.getTime() +
                                        minRequiredDate * 24 * 60 * 60 * 1000
                                    )
                                  : null
                              }
                            />
                          </div>
                        </div>
                      </Col>

                      <Col lg={12}>
                        <Form>
                          <Form.Group className="mt-2">
                            <InputGroup>
                              <Form.Control
                                as="select"
                                name="residentialTypeId"
                                value={selectedResidentialType}
                                onChange={handleResidentialTypeChange}
                                required
                                className="border-0 rounded-0 py-2 text-white"
                                style={{ backgroundColor: "#e6be80" }}
                              >
                                <option value="">
                                  Select Residential Type
                                </option>
                                {residentialType.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                              </Form.Control>
                            </InputGroup>
                          </Form.Group>
                        </Form>
                      </Col>

                      <Col className="py-4 text-white">
                        <FullCalendar
                          plugins={[dayGridPlugin, interactionPlugin]}
                          initialView="dayGridMonth"
                          headerToolbar={{
                            start: "title",
                            center: "",
                            end: "today prev,next",
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>

                  <Col lg={6}>
                    <h4 className="text-white">Available Rooms</h4>
                    {allMatchingResidential.length > 0 ? (
                      allMatchingResidential.map((room) => (
                        <div
                          key={room.id}
                          className="border border-white p-3 mb-4"
                        >
                          <h6 className="text-white">{room.name}</h6>
                          <p style={{ color: "#ffffff90" }}>{room.description}</p>
                          <small style={{ color: "#ffffff90" }}>
                            Capacity: {room.capacity}, Available:{" "}
                            {room.availableFrom} to {room.availableTo}
                          </small>
                        </div>
                      ))
                    ) : (
                      <p className="text-white">
                        No rooms available for the selected criteria.
                      </p>
                    )}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

export default Programdetails;
